import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { fetchAdPreview } from '@src/api';

// Live Facebook render of a flagged ad so the reviewer can see the actual
// creative before pausing — mirrors the live preview on the ad-performance page.
const SEVERITY_COLOR = { critical: 'error', high: 'error', medium: 'warning', low: 'default' };
const pct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

export default function PruningPreviewDialog({ flag, busy, onApprove, onDismiss, onClose }) {
  const [iframeHtml, setIframeHtml] = React.useState('');
  const [state, setState] = React.useState('idle'); // idle | loading | done | error

  const adId = flag?.ad_id;
  React.useEffect(() => {
    if (!adId) return undefined;
    let cancelled = false;
    setState('loading');
    setIframeHtml('');
    fetchAdPreview({ ad_id: adId, ad_format: 'MOBILE_FEED_STANDARD' })
      .then((response) => {
        if (cancelled) return;
        const html = response?.data?.iframe_html || '';
        setIframeHtml(html);
        setState(html ? 'done' : 'error');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [adId]);

  if (!flag) return null;
  const m = flag.metrics || {};
  const creative = m.creative || {};
  const fallbackImage = creative.image_url || creative.thumbnail_url || '';

  return (
    <Dialog open={Boolean(flag)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={flag.severity} color={SEVERITY_COLOR[flag.severity] || 'default'} />
          {flag.would_pause ? <Chip size="small" color="error" variant="outlined" label="Recommended" /> : null}
          <span>{flag.ad_name || flag.ad_id}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="caption" color="text.secondary">
            {[flag.client_name, flag.ad_set_name || 'ad set', flag.vertical].filter(Boolean).join(' · ')}
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {m.stage ? `${m.stage} ` : ''}rate {pct(m.ad_rate)} vs peers {pct(m.peer_rate)}
              {' · '}cost ×{Number(m.cost_ratio || 0).toFixed(1)}
              {' · '}creative −{Math.round(m.creative_gap || 0)}
              {' · '}{(Number(m.p_worse || 0) * 100).toFixed(0)}% confident
            </Typography>
            {flag.ai_hypothesis ? (
              <Typography variant="caption" color="text.secondary">🧠 {flag.ai_hypothesis}</Typography>
            ) : null}
          </Box>

          <Box
            sx={{
              minHeight: 520,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'auto',
              bgcolor: 'grey.100',
              '& iframe': { border: 0, display: 'block' },
            }}
          >
            {state === 'loading' ? (
              <CircularProgress size={28} />
            ) : state === 'done' ? (
              <Box dangerouslySetInnerHTML={{ __html: iframeHtml }} />
            ) : fallbackImage ? (
              <Stack spacing={1} sx={{ p: 2, maxWidth: 420 }}>
                <Box
                  component="img"
                  src={fallbackImage}
                  alt={flag.ad_name || 'Ad creative'}
                  sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
                />
                {creative.headline ? <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{creative.headline}</Typography> : null}
                {creative.primary_text ? <Typography variant="body2" color="text.secondary">{creative.primary_text}</Typography> : null}
                <Typography variant="caption" color="text.secondary">
                  Stored creative (live Facebook render unavailable).
                </Typography>
              </Stack>
            ) : (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="body2" color="text.secondary">Live preview unavailable</Typography>
                <Typography variant="caption" color="text.secondary">
                  No Meta ad is reachable for this flag (e.g. a demo flag, or the ad/account is no longer accessible).
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={!!busy}>Close</Button>
        <Button variant="text" disabled={!!busy} onClick={onDismiss}>
          {busy === 'dismissing' ? '…' : 'Dismiss'}
        </Button>
        <Button variant="contained" color="error" disabled={!!busy} onClick={onApprove}>
          {busy === 'approving' ? 'Pausing…' : 'Pause ad'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
